import Web3 from 'web3'
import { getChainFromId, getChainRpcUrls } from './config.mjs'
import { withRpcFailover } from './rpcFailover.mjs'
import { isValidEvmTxHash, normalizeTransactionHash } from './txHash.mjs'

const TRON_CHAIN_ID = 95500

/** 并行问多个 RPC，任一返回 tx 即视为已广播（排除纯浏览器 CORS 误判） */
async function fetchTransactionByHashParallel(chainId, txHash, opts = {}) {
  const hash = normalizeTransactionHash(txHash)
  if (!hash || Number(chainId) === TRON_CHAIN_ID) return null
  const conf = getChainFromId(chainId)
  const urls = getChainRpcUrls(conf).slice(0, opts.maxRpcs ?? 4)
  if (!urls.length) return null

  const probe = async (rpc) => {
    const web3 = new Web3(rpc)
    return await Promise.race([
      web3.eth.getTransaction(hash),
      new Promise((_, r) => setTimeout(() => r(new Error('timeout')), opts.rpcTimeoutMs ?? 8000)),
    ])
  }

  const results = await Promise.allSettled(urls.map((rpc) => probe(rpc)))
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value?.hash) return r.value
  }
  return null
}

/** eth_getTransactionByHash — 交易是否已进入链上/ mempool（公开 RPC 可查） */
export async function fetchTransactionByHash(chainId, txHash) {
  const hash = normalizeTransactionHash(txHash)
  if (!hash || Number(chainId) === TRON_CHAIN_ID) return null
  return await withRpcFailover(chainId, async (rpc) => {
    const web3 = new Web3(rpc)
    return await web3.eth.getTransaction(hash)
  })
}

/**
 * 钱包返回 hash 后，必须在目标链 RPC 上查到 tx，否则视为未广播（假 hash / 错链）。
 */
export async function waitForTransactionOnChain(chainId, txHash, opts = {}) {
  const hash = normalizeTransactionHash(txHash)
  if (!isValidEvmTxHash(hash)) {
    const err = new Error('WC_INVALID_TX_HASH')
    err.code = 'WC_INVALID_TX_HASH'
    throw err
  }

  const timeoutMs = opts.timeoutMs ?? 90000
  const intervalMs = opts.intervalMs ?? 2500
  const expectedFrom = opts.expectedFrom ? String(opts.expectedFrom).toLowerCase() : ''
  const expectedTo = opts.expectedTo ? String(opts.expectedTo).toLowerCase() : ''
  const deadline = Date.now() + timeoutMs
  let tries = 0

  while (Date.now() < deadline) {
    tries += 1
    try {
      let tx = await fetchTransactionByHashParallel(chainId, hash, { maxRpcs: 5, rpcTimeoutMs: 8000 })
      if (!tx) {
        tx = await fetchTransactionByHash(chainId, hash)
      }
      if (tx && tx.hash) {
        if (expectedFrom && String(tx.from || '').toLowerCase() !== expectedFrom) {
          const err = new Error('TX_FROM_MISMATCH')
          err.code = 'TX_FROM_MISMATCH'
          err.transactionHash = hash
          throw err
        }
        if (expectedTo && String(tx.to || '').toLowerCase() !== expectedTo) {
          const err = new Error('TX_TO_MISMATCH')
          err.code = 'TX_TO_MISMATCH'
          err.transactionHash = hash
          throw err
        }
        console.info('[hieswap] tx found on chain', {
          chainId: Number(chainId),
          hash,
          tries,
          blockNumber: tx.blockNumber ?? 'pending',
        })
        return tx
      }
    } catch (e) {
      if (e?.code === 'TX_FROM_MISMATCH' || e?.code === 'TX_TO_MISMATCH') throw e
      console.warn('[hieswap] tx broadcast poll', Number(chainId), e?.message || e)
    }
    await new Promise((r) => setTimeout(r, intervalMs))
  }

  const err = new Error('TX_NOT_BROADCAST')
  err.code = 'TX_NOT_BROADCAST'
  err.transactionHash = hash
  err.chainId = Number(chainId)
  throw err
}
